import React, { useEffect, useMemo, useState } from "react";
import {
  FaBuildingShield,
  FaArrowDown,
  FaArrowRightLong,
  FaClipboardCheck,
  FaFileCircleCheck,
  FaFileContract,
  FaScaleBalanced,
  FaXmark
} from "react-icons/fa6";
import naicsComplianceData from "../data/naicsComplianceData.json";
import { validateNaicsCode } from "../utils/naicsComplianceUtils";
import "../styles/NaicsComplianceCheckerVariant.css";

function listKey(title) {
  return title.toLowerCase().replace(/\s+/g, "-");
}

function normalizeText(value) {
  return (value || "").toLowerCase().replace(/\s+/g, " ").trim();
}

function findItemLinks(result, type, label) {
  const candidates = result?.item_links?.[type];
  if (!Array.isArray(candidates) || !label) return null;
  const target = normalizeText(label);
  return candidates.find((item) => normalizeText(item.name) === target) || null;
}

function shortText(value, max = 72) {
  const text = (value || "").trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
}

function NaicsComplianceCheckerVariant() {
  const [naicsCode, setNaicsCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [matchType, setMatchType] = useState("");
  const [activeDetail, setActiveDetail] = useState(null);

  const normalizedCode = useMemo(() => naicsCode.trim(), [naicsCode]);

  const supportedCount = useMemo(
    () => Object.keys(naicsComplianceData || {}).length,
    []
  );

  const quickCodes = useMemo(() => {
    const entries = Object.entries(naicsComplianceData || {});
    const preferredCodes = ["722513", "311611", "312111", "311812"];
    const selected = preferredCodes
      .map((code) => [code, naicsComplianceData[code]])
      .filter((entry) => entry[1]);

    if (selected.length < 4) {
      const fallback = entries.filter(
        ([code]) => !selected.some(([selectedCode]) => selectedCode === code)
      );
      selected.push(...fallback.slice(0, 4 - selected.length));
    }

    return selected.slice(0, 4).map(([code, data]) => ({
      code,
      label: data.business_type || data.category || "Food Business"
    }));
  }, []);

  const handleSearch = async () => {
    const validationError = validateNaicsCode(normalizedCode);
    if (validationError) {
      setError(validationError);
      setResult(null);
      setMatchType("");
      setActiveDetail(null);
      return;
    }

    setError("");
    setLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      const exactRecord = naicsComplianceData[normalizedCode];
      if (!exactRecord) {
        setError("No exact NAICS record found in dataset. Please enter a supported 6-digit code.");
        setResult(null);
        setMatchType("");
        setActiveDetail(null);
        return;
      }

      setResult(exactRecord);
      setMatchType("exact");
      setActiveDetail(null);
    } catch (err) {
      console.error("Compliance lookup failed:", err);
      setError("Something went wrong while generating compliance suggestions.");
      setResult(null);
      setMatchType("");
      setActiveDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const riskClass = (result?.risk_level || "").toLowerCase();

  const renderPartLinks = (partKey) => {
    if (!result) return null;
    const configuredLinks = result.part_links?.[partKey];
    if (!configuredLinks) return null;
    const onlineUrl = configuredLinks.online;
    const onlineLabel = configuredLinks.online_label || "Official website";
    const pdfUrl = configuredLinks.pdf;
    if (!onlineUrl && !pdfUrl) return null;

    return (
      <div className="naics-part-links">
        {pdfUrl ? (
          <a href={pdfUrl} target="_blank" rel="noreferrer">
            PDF guide
          </a>
        ) : null}
        {onlineUrl ? (
          <a href={onlineUrl} target="_blank" rel="noreferrer">
            {onlineLabel}
          </a>
        ) : null}
      </div>
    );
  };

  const renderItemApplyLink = (type, label) => {
    if (!result) return null;
    const links = findItemLinks(result, type, label);
    if (!links?.apply) return null;
    return (
      <a className="naics-inline-link" href={links.apply} target="_blank" rel="noreferrer">
        Apply / official link
      </a>
    );
  };

  const roadmapStages = useMemo(() => {
    if (!result) return [];
    return [
      {
        key: "stage-1",
        title: "Stage 1: Business Setup",
        subtitle: "Complete core registrations first",
        items: result.registrations || [],
        itemType: "registrations",
        linkKey: "registrations"
      },
      {
        key: "stage-2",
        title: "Stage 2: Permit Approvals",
        subtitle: "Apply for permit clearances",
        items: result.permits || [],
        itemType: "permits",
        linkKey: "permits"
      },
      {
        key: "stage-3",
        title: "Stage 3: Compliance Documents",
        subtitle: "Prepare required records and plans",
        items: result.documents || [],
        itemType: null,
        linkKey: "documents"
      },
      {
        key: "stage-4",
        title: "Stage 4: Readiness & Inspection",
        subtitle: result.inspection_type || "Inspection readiness",
        items: result.next_steps || [],
        itemType: null,
        linkKey: "next_steps"
      }
    ].filter((stage) => stage.items.length);
  }, [result]);

  const topPriorities = useMemo(() => {
    if (!result?.next_steps?.length) return [];
    return result.next_steps.slice(0, 3).map((step) => shortText(step, 66));
  }, [result]);

  const summarySections = useMemo(() => {
    if (!result) return [];
    return [
      {
        key: "registrations",
        title: "Registrations",
        subtitle: "Core legal registrations",
        items: result.registrations || [],
        itemType: "registrations",
        linkKey: "registrations",
        panelClass: "registrations-panel"
      },
      {
        key: "permits",
        title: "Permits",
        subtitle: "Operational approvals",
        items: result.permits || [],
        itemType: "permits",
        linkKey: "permits",
        panelClass: "permits-panel"
      },
      {
        key: "documents",
        title: "Documents",
        subtitle: "Required plans and logs",
        items: result.documents || [],
        itemType: null,
        linkKey: "documents",
        panelClass: "docs-panel"
      },
      {
        key: "regulations",
        title: "Regulations",
        subtitle: "Applicable legal rules",
        items: result.regulations || [],
        itemType: null,
        linkKey: "regulations",
        panelClass: "regulations-panel"
      },
      {
        key: "agencies",
        title: "Agencies",
        subtitle: "Primary regulators",
        items: result.agencies || [],
        itemType: null,
        linkKey: "agencies",
        panelClass: "agencies-panel"
      },
      {
        key: "next_steps",
        title: "Next Steps",
        subtitle: "Action plan sequence",
        items: result.next_steps || [],
        itemType: null,
        linkKey: "next_steps",
        panelClass: "steps-panel",
        ordered: true
      }
    ].filter((section) => section.items.length);
  }, [result]);

  const activeSection = useMemo(
    () => summarySections.find((section) => section.key === activeDetail) || null,
    [summarySections, activeDetail]
  );

  useEffect(() => {
    const onEsc = (event) => {
      if (event.key === "Escape") setActiveDetail(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  const sectionIcon = (key) => {
    if (key === "registrations") return <FaFileContract />;
    if (key === "permits") return <FaFileCircleCheck />;
    if (key === "documents") return <FaClipboardCheck />;
    if (key === "regulations") return <FaScaleBalanced />;
    if (key === "agencies") return <FaBuildingShield />;
    return <FaArrowRightLong />;
  };

  return (
    <div className="naics-variant">
      <section className="naics-variant-search">
        <h3>NAICS Compliance Rules Explorer</h3>
        <p>
          Enter a 6-digit NAICS code to get rules, regulations, registrations, permits, and
          compliance documents for that business category.
        </p>
        <p className="naics-variant-data-note">
          Real dataset loaded: {supportedCount} mapped NAICS codes.
        </p>

        <div className="naics-variant-row">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            className="naics-variant-input"
            placeholder="Example: 311611"
            value={naicsCode}
            onChange={(e) => setNaicsCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            aria-label="NAICS code"
          />
          <button type="button" className="naics-variant-btn" onClick={handleSearch} disabled={loading}>
            {loading ? "Checking..." : "Show Rules"}
          </button>
        </div>

        <div className="naics-variant-chips">
          {quickCodes.map((item) => (
            <button
              key={item.code}
              type="button"
              className="naics-chip"
              onClick={() => {
                setNaicsCode(item.code);
                setError("");
              }}
            >
              <strong>{item.code}</strong> {item.label}
            </button>
          ))}
        </div>

        {error ? <div className="naics-variant-message error">{error}</div> : null}
        {!error && loading ? (
          <div className="naics-variant-message info">Loading compliance suggestions...</div>
        ) : null}
      </section>

      {result ? (
        <section className="naics-variant-results">
          <div className="naics-variant-summary">
            <div>
              <h4>{result.business_type}</h4>
              <p>
                NAICS {normalizedCode} | {result.category} | {result.inspection_type}
              </p>
              <span className="match-pill">
                {matchType === "exact" ? "Exact Dataset Match" : "Match"}
              </span>
            </div>
            <span className={`risk-pill ${riskClass}`}>{result.risk_level} Risk</span>
          </div>

          <div className="naics-kpi-strip">
            <div className="naics-kpi-box">
              <span>Agencies</span>
              <strong>{result.agencies?.length || 0}</strong>
            </div>
            <div className="naics-kpi-box">
              <span>Regulations</span>
              <strong>{result.regulations?.length || 0}</strong>
            </div>
            <div className="naics-kpi-box">
              <span>Registrations</span>
              <strong>{result.registrations?.length || 0}</strong>
            </div>
            <div className="naics-kpi-box">
              <span>Permits</span>
              <strong>{result.permits?.length || 0}</strong>
            </div>
            <div className="naics-kpi-box">
              <span>Documents</span>
              <strong>{result.documents?.length || 0}</strong>
            </div>
          </div>

          {roadmapStages.length ? (
            <div className="naics-roadmap-wrap">
              <div className="naics-panel roadmap-panel">
                <h5>Compliance Roadmap For This NAICS Code</h5>
                <p className="naics-links-note">
                  Summary first: open one section below for full details.
                </p>
                {topPriorities.length ? (
                  <div className="naics-priority-strip">
                    {topPriorities.map((priority) => (
                      <span key={listKey(priority)} className="naics-priority-chip">
                        {priority}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="naics-roadmap-grid">
                  {roadmapStages.map((stage, index) => (
                    <div key={stage.key} className="roadmap-stage">
                      <div className="roadmap-stage-head">
                        <span className="roadmap-index">{index + 1}</span>
                        <div>
                          <h6>{stage.title}</h6>
                          <p>{stage.subtitle}</p>
                        </div>
                      </div>
                      <p className="roadmap-stage-meta">{stage.items.length} items</p>
                      {stage.items.length ? (
                        <p className="roadmap-stage-preview">
                          {shortText(stage.items[0], 78)}
                          {stage.items.length > 1 ? ` (+${stage.items.length - 1} more)` : ""}
                        </p>
                      ) : null}
                      {index < roadmapStages.length - 1 ? (
                        <div className="roadmap-arrow">
                          <FaArrowRightLong />
                          <FaArrowDown />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <div className="naics-summary-grid">
            {summarySections.map((section) => (
              <div key={section.key} className={`naics-summary-card ${section.panelClass}`}>
                <div className="naics-summary-head">
                  <h6>{section.title}</h6>
                  <strong>{section.items.length}</strong>
                </div>
                <p className="naics-summary-preview">
                  {section.subtitle}
                </p>
                <button
                  type="button"
                  className="naics-expand-btn"
                  onClick={() =>
                    setActiveDetail(section.key)
                  }
                >
                  View details
                </button>
              </div>
            ))}
          </div>

          {activeSection ? (
            <div
              className="naics-modal-backdrop"
              onClick={() => setActiveDetail(null)}
              role="button"
              tabIndex={0}
            >
              <div
                className="naics-modal-card"
                role="dialog"
                aria-modal="true"
                aria-label={`${activeSection.title} details`}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="naics-modal-head">
                  <h5>
                    {sectionIcon(activeSection.key)} {activeSection.title}
                  </h5>
                  <button
                    type="button"
                    className="naics-modal-close"
                    onClick={() => setActiveDetail(null)}
                    aria-label="Close details"
                  >
                    <FaXmark />
                  </button>
                </div>
                <p className="naics-links-note">
                  Showing exact dataset values for NAICS {normalizedCode}.
                </p>
                {renderPartLinks(activeSection.linkKey)}
                <div className="naics-modal-body">
                  {activeSection.ordered ? (
                    <ol>
                      {activeSection.items.map((item, index) => (
                        <li className="naics-item-row" key={listKey(`${activeSection.key}-${item}`)}>
                          <span className="step-index">{index + 1}</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <ul>
                      {activeSection.items.map((item) => (
                        <li className="naics-item-row" key={listKey(`${activeSection.key}-${item}`)}>
                          <span>{item}</span>
                          {activeSection.itemType ? renderItemApplyLink(activeSection.itemType, item) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export default NaicsComplianceCheckerVariant;
